import React, { useContext, useMemo, useState } from "react";
import { UserContext } from "../../../../context";
import { Separator, Switch, Text } from "../../../../components";
import {
  BillingStyle,
  MontlyYearlyStyle,
  MontlyYearlyStyleLeft,
  MontlyYearlyStyleRight,
  PricingButtonStyle,
  PricingCardStyle,
  PricingContainer,
  PricingFeatureIconStyle,
  PricingFeatureStyle,
  PricingFreeButtonStyle,
  SelectedTagStyle,
  TrialBannerStyle,
} from "../../styles";
import {
  CheckCircleIcon,
  InfiniteSpinnerIcon,
  PlainCheckCircleIcon,
} from "../../../../components/icons";
import { config } from "../../../../config";
import { E_Route, ESubscriptionPlan } from "@repo-sapiens/constants";
import { PricingOption, PricingTabs } from "../../../onboarding/components";
import { css } from "@emotion/css";

const quantity = {
  monthly: 1,
  quarterly: 3,
  yearly: 12,
};

type PaymentProps = {
  currentPlan: {
    isTrial: boolean;
    plan: ESubscriptionPlan;
    billingCycle?: string;
    hasActiveSubscription?: boolean;
    cancelAtTheEndOfThePeriod?: boolean;
  };
  pricing: {
    monthlyPrice: number;
    quarterlyPrice: number;
    annualPrice: number;
  };
  vipUser?: boolean;
};
export const Payment: React.FC<PaymentProps> = ({
  currentPlan,
  pricing,
  vipUser,
}) => {
  const { state: userState } = useContext(UserContext);
  const [loading, setLoading] = useState(false);
  const [pricingOption, setPricingOption] =
    React.useState<PricingOption>('quarterly');

  const { monthlyPrice, quarterlyPrice, annualPrice } = pricing;

  const selectedPrice = useMemo(() => {
    switch (pricingOption) {
      case 'yearly':
        return annualPrice / 12;
      case 'quarterly':
        return quarterlyPrice / 3;
      default:
        return monthlyPrice;
    }
  }, [pricingOption, monthlyPrice, annualPrice, quarterlyPrice]);

  const handleSelectPricingOption = (option: PricingOption) => {
    setPricingOption(option);
  };

  const { isTrial, plan, hasActiveSubscription, cancelAtTheEndOfThePeriod } = currentPlan;

  const noPlan = plan !== ESubscriptionPlan.Pro || isTrial || !hasActiveSubscription || cancelAtTheEndOfThePeriod;
  
  const getButtonProps = (pricingOption: PricingOption) => {
    if (currentPlan.billingCycle === pricingOption) {
      return {
        text: "Current plan",
        disabled: true,
      };
    } else if (!noPlan) {
      return {
        text: "Change plan",
        disabled: false,
      };
    } 
    return {
      text: "Upgrade plan",
      disabled: false,
    };
  
  }

  const buttonProps = getButtonProps(pricingOption);

  return (
    <div className={BillingStyle}>
        <PricingTabs
          activeTab={pricingOption}
          onSelect={handleSelectPricingOption}
        />

        <Separator.Horizontal spacing={3} />

        <PricingItem price={selectedPrice} />

        <Separator.Horizontal spacing={3} />

        <form
          action={`${config.apiUrl}/billing/create-checkout-session`}
          method="POST"
          style={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            justifyContent: 'flex-start',
          }}
        >
          <input type="hidden" name="userId" value={userState?._id} />
          <input type="hidden" name="redirect" value={E_Route.ManageBilling} />
          <input type="hidden" name="failureRedirect" value={E_Route.ManageBilling} />
          <input
            type="hidden"
            name="quantity"
            value={quantity[pricingOption]}
          />

          <button
            type="submit"
            onClick={() => setLoading(true)}
            className={PricingButtonStyle}
            disabled={buttonProps.disabled}
            style={{
              cursor: loading ? "progress" : "",
              pointerEvents: loading ? "none" : "auto",
            }}
          >
            {buttonProps.text}
            {loading ? (
              <InfiniteSpinnerIcon
                fill="dark"
                style={{ height: "16px", marginLeft: "8px" }}
              />
            ) : null}
          </button>
        </form>
    </div>
  );
};

const pricingTexts = [
  'Organize and archive emails into categories',
  'Suggests meeting times from your Google Calendar',
  'Join meetings and summarizes notes and next steps',
  'Meeting prep',
  'Daily podcast',
  'Action items (coming soon)',
];

type PricingItemProps = {
  price: number;
}
function PricingItem({ price }: PricingItemProps) {
  return (
    <div
      className={css`
        border-radius: 24px;
        border: 1px solid #29282f;
        background: #161617;
        width: 100%;
        height: 100%;
        display: flex;
        flex-direction: column;
        justify-content: flex-start;
        align-items: flex-start;
        padding: 32px;
        margin-top: 42px;
        margin-bottom: 18px;
        @media (max-width: 648px) {
          margin-bottom: 24px;
        }
      `}
    >
      <div
        className={css`
        width: 100%;
        display: grid;
          grid-template-columns: 35% 1fr;
          gap: 80px;
          @media (max-width: 648px) {
            grid-template-columns: 1fr;
            gap: 16px;
          }
        `}
      >
        <div
          className={css`
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
          `}
        >
          <Text.H2 style={{ fontSize: '72px' }} color="highlight/1">${price} <br /><span style={{ fontSize: '21px', color: '#fff' }}>/ month</span></Text.H2>
        </div>
        
        <div
          className={css`
            display: block;
            width: 100%;
          `}
        >
          {pricingTexts.map((text, index) => (
            <div
              key={index}
              className={css`
                width: 100%;
                padding: 12px 0;
                border-bottom: 1px solid #212121;
                border-bottom-width: ${index === pricingTexts.length - 1
                  ? '0'
                  : '1px'};
              `}
            >
              <Text.BodySM color="secondary" style={{ fontSize: '15px' }}>
                {text}
              </Text.BodySM>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
